import { useMemo } from 'react';

import { routes } from '@aline/routes';
import type { ApolloLink } from '@apollo/client';
import { useMatch } from 'react-router-dom';
import { Apollo } from 'src/gql/generated';
import { createCommunityCategoryURLId, documentTypename, workflowTypename } from 'src/helpers';

export function useCommunityWorkflows(
  options?: Parameters<typeof Apollo.useGetCommunityWorkflowsQuery>[0]
) {
  const { data, loading } = Apollo.useGetCommunityWorkflowsQuery(options);
  return { communityWorkflows: data?.communityWorkflows ?? [], loading };
}

export function useCommunityCategories(
  options?: Parameters<typeof Apollo.useGetCommunityCategoriesQuery>[0]
) {
  const { data, loading } = Apollo.useGetCommunityCategoriesQuery(options);
  return { communityCategories: data?.communityCategories ?? [], loading };
}

export function useSelectedCommunityCategory() {
  const matchCommunityRoute = useMatch({ end: false, path: routes.CommunityCategoryIndex });
  const { communityCategories } = useCommunityCategories();
  const selectedCategory = useMemo(() => {
    return communityCategories.find(category => {
      const categoryURLId = createCommunityCategoryURLId(category);
      return categoryURLId === matchCommunityRoute?.params.categoryId;
    });
  }, [communityCategories, matchCommunityRoute]);

  return { selectedCategory: selectedCategory ?? null };
}

export function useSupportedLicenses(
  options?: Parameters<typeof Apollo.useGetSupportedLicensesQuery>[0]
) {
  const { data, loading } = Apollo.useGetSupportedLicensesQuery(options);
  return { loading, supportedLicenses: data?.supportedLicenses ?? [] };
}

export function useCommunityTemplates(
  options?: Parameters<typeof Apollo.useGetCommunityTemplatesQuery>[0]
) {
  const { data, loading } = Apollo.useGetCommunityTemplatesQuery(options);
  return { communityTemplates: data?.communityTemplates ?? [], loading };
}

export function useImportCommunityWorkflow(workflowId: UUID): {
  importWorkflow: () => Promise<ApolloLink.Result<Apollo.ImportCommunityWorkflowMutation>>;
  loading: boolean;
} {
  const numTimesImportedKey: keyof Apollo.Workflow = 'numTimesImported';
  const requesterImportedKey: keyof Apollo.Workflow = 'requesterImportedWorkflowIds';
  const [importCommunityWorkflow, { loading }] = Apollo.useImportCommunityWorkflowMutation({
    variables: { input: { workflowId } },
  });

  return {
    importWorkflow: () => {
      // Variables provided at hook level
      return importCommunityWorkflow({
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              [numTimesImportedKey]: (num = 0) => num + 1,
              [requesterImportedKey]: (ids = []) => {
                return data && Array.isArray(ids)
                  ? [...ids, data.importCommunityWorkflow.workflow.id]
                  : ids;
              },
            },
            id: cache.identify({ __typename: workflowTypename, id: workflowId }),
          });
        },
        variables: { input: { workflowId } },
      });
    },
    loading,
  };
}

export function useImportTemplate() {
  const numTimesImportedKey: keyof Apollo.Document = 'numTimesImported';
  const requesterImportedKey: keyof Apollo.Document = 'requesterImportedDocumentIds';
  const [duplicateTemplate, { loading }] = Apollo.useDuplicateTemplateMutation();

  return {
    importTemplate: (templateId: UUID) => {
      return duplicateTemplate({
        update: (cache, { data }) => {
          cache.modify({
            fields: {
              [numTimesImportedKey]: (num = 0) => num + 1,
              [requesterImportedKey]: (ids = []) => {
                return data?.duplicateTemplate.document?.id && Array.isArray(ids)
                  ? [...ids, ...data.duplicateTemplate.document.id]
                  : ids;
              },
            },
            id: cache.identify({ __typename: documentTypename, id: templateId }),
          });
        },
        variables: { input: { templateId } },
      });
    },
    loading,
  };
}
